import * as ents from '@attain-apps/entities'

import type { Data } from './data'
import type { Edge } from './edge'

export interface Change {
  readonly nodes: {
    readonly added: readonly ents.core.display.tree.Node[]
    readonly edited: readonly {
      readonly from: ents.core.display.tree.Node
      readonly to: ents.core.display.tree.Node
    }[]
    readonly deleted: readonly ents.core.display.tree.Node[]
  }
  readonly edges: {
    readonly added: readonly Edge[]
    readonly deleted: readonly Edge[]
  }
}
export namespace Change {
  /**
   * Produces a complete {@link Change} from the given partial {@link Change}
   */
  export function of(
    change: Partial<{
      [K in keyof Change]: Partial<Change[K]>
    }>,
  ): Change {
    return {
      edges: {
        added: change.edges?.added ?? [],
        deleted: change.edges?.deleted ?? [],
      },
      nodes: {
        added: change.nodes?.added ?? [],
        deleted: change.nodes?.deleted ?? [],
        edited: change.nodes?.edited ?? [],
      },
    }
  }

  /**
   * Creates the opposite {@link Change} from the given one
   *
   * Useful for determining how to undo a {@link Change}
   */
  export function inverse(change: Change): Change {
    return {
      edges: {
        added: change.edges.deleted,
        deleted: change.edges.added,
      },
      nodes: {
        added: change.nodes.deleted,
        deleted: change.nodes.added,
        edited: change.nodes.edited.map(({ from, to }) => ({
          from: to,
          to: from,
        })),
      },
    }
  }

  /**
   * Indicates that a {@link Change} has no effect
   */
  export function isEmpty(change: Change): boolean {
    return (
      change.edges.added.length === 0 &&
      change.edges.deleted.length === 0 &&
      change.nodes.added.length === 0 &&
      change.nodes.deleted.length === 0 &&
      change.nodes.edited.length === 0
    )
  }

  /**
   * Returns a new {@link Data} resulting from applying the given {@link Change}
   * to the given {@link Data}
   */
  export function appliedTo(data: Data, change: Change): Data {
    // Creates a shallow copy that we can mutate without affecting the original
    data = { ...data }
    /* ---------------------------------- Nodes --------------------------------- */
    // We delete first, since we may add some of these back in the below
    // steps
    if (change.nodes.deleted.length) {
      data.nodes = data.nodes.filter(
        node =>
          !change.nodes.deleted.some(
            deletedNode =>
              ents.core.display.tree.Node.Id.get(deletedNode) ===
              ents.core.display.tree.Node.Id.get(node),
          ),
      )
    }
    // We then add, since we may overwrite the additions in the following
    // edits
    if (change.nodes.added.length) {
      data.nodes = [...data.nodes, ...change.nodes.added]
    }
    // Finally, once we have settled on the correct nodes, we apply any
    // edits
    if (change.nodes.edited.length) {
      data.nodes = data.nodes.map(
        node =>
          change.nodes.edited.find(
            edit =>
              ents.core.display.tree.Node.Id.get(edit.from) ===
              ents.core.display.tree.Node.Id.get(node),
          )?.to ?? node,
      )
    }
    /* ---------------------------------- Edges --------------------------------- */
    // We delete first, for the same reason as above
    if (change.edges.deleted.length) {
      data.edges = data.edges.filter(
        edge =>
          !change.edges.deleted.some(
            deletedEdge =>
              deletedEdge.parentId === edge.parentId &&
              deletedEdge.childId === edge.childId,
          ),
      )
    }
    if (change.edges.added.length) {
      data.edges = [...data.edges, ...change.edges.added]
    }

    return data
  }
}
